import { z } from "zod";
import { decimalByteCountSchema } from "./seedance.js";

export const seedanceGenerationStateSchema = z.enum([
  "queued",
  "leased",
  "submitting",
  "submitted",
  "running",
  "submit_uncertain",
  "succeeded",
  "failed",
  "cancelled"
]);

export type SeedanceGenerationState = z.infer<typeof seedanceGenerationStateSchema>;

const uuidSchema = z.string().uuid();
const fingerprintSchema = z.string().regex(/^[0-9a-f]{64}$/u);
const instantSchema = z.string().datetime({ offset: true });

export const seedanceGenerationJobSchema = z.strictObject({
  id: uuidSchema,
  ownerId: uuidSchema,
  projectId: uuidSchema,
  canvasId: uuidSchema,
  canvasRevision: decimalByteCountSchema,
  seedanceNodeId: uuidSchema,
  clientCommandId: z.string().trim().min(1).max(160),
  requestFingerprint: fingerprintSchema,
  compilerVersion: z.string().trim().min(1).max(120),
  capabilityVersion: z.string().trim().min(1).max(120),
  snapshot: z.record(z.string(), z.unknown()),
  state: seedanceGenerationStateSchema,
  outputAssetIds: z.array(uuidSchema).max(100).default([]),
  workerId: z.string().trim().min(1).max(160).optional(),
  leaseExpiresAt: instantSchema.optional(),
  upstreamTaskId: z.string().trim().min(1).max(320).optional(),
  submitUncertainReason: z.string().trim().min(1).max(1_000).optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema
});

export type SeedanceGenerationJobView = z.infer<typeof seedanceGenerationJobSchema>;

export const seedanceGenerationCreateResponseSchema = z.strictObject({
  job: seedanceGenerationJobSchema
});

export const seedanceGenerationReconciliationJobSchema = z.strictObject({
  id: uuidSchema,
  state: seedanceGenerationStateSchema,
  updatedAt: instantSchema
});

export type SeedanceGenerationReconciliationJobView = z.infer<typeof seedanceGenerationReconciliationJobSchema>;

export const seedanceGenerationEventSchema = z.strictObject({
  id: uuidSchema,
  generationId: uuidSchema,
  ownerId: uuidSchema,
  actorUserId: uuidSchema.optional(),
  eventType: z.string().trim().min(1).max(160),
  state: seedanceGenerationStateSchema,
  requestId: z.string().trim().min(1).max(160),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: instantSchema
});

export type SeedanceGenerationEventView = z.infer<typeof seedanceGenerationEventSchema>;

export const seedanceUsageLedgerSchema = z.strictObject({
  generationId: uuidSchema,
  requestFingerprint: fingerprintSchema,
  inputTokens: decimalByteCountSchema.optional(),
  outputTokens: decimalByteCountSchema.optional(),
  totalTokens: decimalByteCountSchema.optional(),
  recordedAt: instantSchema
});

export type SeedanceUsageLedgerView = z.infer<typeof seedanceUsageLedgerSchema>;

const generationUsageViewSchema = z.strictObject({
  providerReported: z.boolean(),
  inputTokens: decimalByteCountSchema.optional(),
  outputTokens: decimalByteCountSchema.optional(),
  totalTokens: decimalByteCountSchema.optional(),
  recordedAt: instantSchema.optional()
});

export const seedanceGenerationHistoryItemSchema = seedanceGenerationJobSchema.extend({
  projectName: z.string().trim().min(1).max(120),
  ownerDisplayName: z.string().trim().min(1).max(255).optional(),
  usage: generationUsageViewSchema
});

export const seedanceGenerationHistorySummarySchema = z.strictObject({
  inputTokens: decimalByteCountSchema,
  outputTokens: decimalByteCountSchema,
  totalTokens: decimalByteCountSchema,
  providerNotReportedCount: z.number().int().nonnegative()
});

export const seedanceGenerationHistoryResponseSchema = z.strictObject({
  items: z.array(seedanceGenerationHistoryItemSchema),
  summary: seedanceGenerationHistorySummarySchema,
  nextCursor: z.string().min(1).max(2_048).optional()
});

export const seedanceAdminGenerationHistoryItemSchema = z.strictObject({
  id: uuidSchema,
  ownerId: uuidSchema,
  projectId: uuidSchema,
  projectName: z.string().trim().min(1).max(120),
  ownerDisplayName: z.string().trim().min(1).max(255),
  state: seedanceGenerationStateSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
  usage: generationUsageViewSchema
});

export const seedanceAdminGenerationHistoryResponseSchema = z.strictObject({
  items: z.array(seedanceAdminGenerationHistoryItemSchema),
  summary: seedanceGenerationHistorySummarySchema,
  nextCursor: z.string().min(1).max(2_048).optional()
});

export type SeedanceGenerationHistoryItemView = z.infer<typeof seedanceGenerationHistoryItemSchema>;
export type SeedanceGenerationHistorySummaryView = z.infer<typeof seedanceGenerationHistorySummarySchema>;
export type SeedanceGenerationHistoryResponse = z.infer<typeof seedanceGenerationHistoryResponseSchema>;
export type SeedanceAdminGenerationHistoryResponse = z.infer<typeof seedanceAdminGenerationHistoryResponseSchema>;

export const auditEventViewSchema = z.strictObject({
  id: uuidSchema,
  actorUserId: uuidSchema.optional(),
  actorDisplayName: z.string().trim().min(1).max(255).optional(),
  ownerUserId: uuidSchema.optional(),
  ownerDisplayName: z.string().trim().min(1).max(255).optional(),
  eventType: z.string().trim().min(1).max(160),
  targetType: z.string().trim().min(1).max(120),
  targetId: uuidSchema.optional(),
  requestId: z.string().trim().min(1).max(160),
  createdAt: instantSchema
});

export const auditEventListResponseSchema = z.strictObject({
  items: z.array(auditEventViewSchema),
  nextCursor: z.string().min(1).max(2_048).optional()
});

export type AuditEventView = z.infer<typeof auditEventViewSchema>;
export type AuditEventListResponse = z.infer<typeof auditEventListResponseSchema>;
